/**
 * useGetHubCompetitions - Fetch competitions linked to a hub
 *
 * Uses the GET /hubs/slug/:slug/competitions endpoint which:
 * - Queries all 7 competition types (TOURNAMENT, LEAGUE, LADDER, LEADERBOARD, GUILD, CHALLENGE, CIRCUIT)
 * - Returns unified response format with game info, prize pools, dates
 * - Supports filtering by type and activeOnly
 * - Single API call vs. the old approach of 3+ separate calls
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { Token, PaginationInfo, DropsApi } from "@devour/client";

/*
 * ============================================================================
 * Constants
 * ============================================================================
 */

/** Query key prefix for hub competitions queries */
export const HUB_COMPETITIONS_QUERY_KEY = "hub-competitions" as const;

/**
 * Status values considered "active" for competitions
 */
const ACTIVE_COMPETITION_STATUSES = [
    "ACTIVE",
    "IN_PROGRESS",
    "REGISTRATION",
    "ONGOING",
    "LIVE",
    "SCHEDULED",
] as const;

/*
 * ============================================================================
 * Types
 * ============================================================================
 */

/**
 * Competition types supported by Hub competitions endpoint
 */
export type HubCompetitionType =
    | "TOURNAMENT"
    | "LEAGUE"
    | "LADDER"
    | "LEADERBOARD"
    | "GUILD"
    | "CHALLENGE"
    | "CIRCUIT";

/**
 * Challenge structure types for CHALLENGE competitions
 */
export type ChallengeStructure =
    | "HEAD_TO_HEAD"
    | "FREE_FOR_ALL"
    | "KING_OF_HILL"
    | "GAUNTLET"
    | "CREATOR_VS_COMMUNITY";

/**
 * Unified competition response from the API
 * Normalized across all competition types
 *
 * Architecture note: The backend resolves:
 * Hub → Competition umbrellas → items[] → actual documents
 *
 * division and order come from Competition.items[]
 * Everything else comes from the actual document (TournamentModel, LadderModel, etc.)
 */
export interface HubCompetition {
    id: string;
    type: HubCompetitionType;
    name: string;
    description?: string;
    status: string;
    /** Division/label from Competition.items[] (e.g., "Professional", "Amateur", "Open") */
    division?: string;
    /** Display order from Competition.items[] for multi-stage competitions */
    order?: number;
    /** Parent Competition umbrella ID */
    competitionId?: string;
    game?: {
        id: string;
        name: string;
        thumbnailUrl?: string;
    };
    participantCount?: number;
    maxParticipants?: number;
    teamSize?: number;
    prizePool?: {
        total: number;
        currency?: string;
        distribution?: Array<{
            place: number;
            amount?: number;
        }>;
    };
    startDate?: number;
    endDate?: number;
    registrationEndDate?: number;
    createdAt: number;
    updatedAt: number;

    // Challenge-specific fields (only populated for CHALLENGE type)
    /** Challenge structure: HEAD_TO_HEAD, KING_OF_HILL, CREATOR_VS_COMMUNITY, etc. */
    structure?: ChallengeStructure;
    /** Entry type: FREE, ENTRY_FEE, SPONSORED, etc. */
    entryType?: string;
    /** Timing mode: REAL_TIME, SCHEDULED, ASYNC */
    timingMode?: string;
    /** Host type: PLATFORM, CREATOR, PEER, CHAMPION */
    hostType?: string;
    /** Creator user ID for creator-hosted challenges */
    creatorId?: string;
}

export interface GetHubCompetitionsResponse {
    competitions: HubCompetition[];
    paginationInfo: PaginationInfo;
}

export interface HubCompetitionsFilter {
    /** Filter by specific competition type */
    type?: HubCompetitionType;
    /** Only return active/live competitions */
    activeOnly?: boolean;
    /** Pagination limit */
    limit?: number;
    /** Pagination offset */
    offset?: number;
}

/*
 * ============================================================================
 * Query Key Factory
 * ============================================================================
 */

/**
 * Query key factory for hub competitions
 * Enables easy cache invalidation at different granularities
 *
 * @example
 * // Invalidate all hub competitions
 * queryClient.invalidateQueries({ queryKey: hubCompetitionsKeys.all });
 *
 * // Invalidate for specific hub
 * queryClient.invalidateQueries({ queryKey: hubCompetitionsKeys.bySlug("snapple") });
 */
export const hubCompetitionsKeys = {
    all: [HUB_COMPETITIONS_QUERY_KEY] as const,
    bySlug: (slug: string) => [HUB_COMPETITIONS_QUERY_KEY, slug] as const,
    withFilter: (
        slug: string,
        filter: HubCompetitionsFilter,
        userId?: string,
    ) => [
        HUB_COMPETITIONS_QUERY_KEY,
        slug,
        filter.type ?? "all",
        filter.activeOnly ?? false,
        filter.limit ?? 20,
        filter.offset ?? 0,
        userId ?? "unauthenticated",
    ] as const,
};

/*
 * ============================================================================
 * Fetch Function
 * ============================================================================
 */

/**
 * Fetch hub competitions from the API
 * @throws Error with detailed message from API or network error
 */
async function fetchHubCompetitions(
    slug: string,
    filter: HubCompetitionsFilter,
    fullToken?: Token,
): Promise<GetHubCompetitionsResponse> {
    const data = await new DropsApi(getConfig(fullToken)).getDropsSlugCompetitions({
        slug,
        type: filter.type as any,
        activeOnly: filter.activeOnly as any,
        limit: filter.limit,
        offset: filter.offset,
    });
    const result = data as unknown as GetHubCompetitionsResponse;
    if (!result.competitions || !Array.isArray(result.competitions)) {
        throw new Error("Invalid response format from server");
    }
    return result;
}

/*
 * ============================================================================
 * Hook
 * ============================================================================
 */

/**
 * Fetch competitions linked to a hub
 *
 * @param slug - Hub URL slug
 * @param filter - Optional filters (type, activeOnly, pagination)
 *
 * @example
 * // Get all competitions for a hub
 * const { data } = useGetHubCompetitions("snapple");
 *
 * // Get only tournaments
 * const { data } = useGetHubCompetitions("snapple", { type: "TOURNAMENT" });
 *
 * // Get only active competitions
 * const { data } = useGetHubCompetitions("snapple", { activeOnly: true });
 */
export function useGetHubCompetitions(slug?: string, filter: HubCompetitionsFilter = {}) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: hubCompetitionsKeys.withFilter(slug ?? "", filter, fullToken?.id),
        queryFn: () => fetchHubCompetitions(slug!, filter, fullToken),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes - garbage collect stale entries
        enabled: Boolean(slug),
        placeholderData: keepPreviousData,
    });
}

/*
 * ============================================================================
 * Helper Functions
 * ============================================================================
 */

/**
 * Helper to filter competitions by type from cached data
 * Useful when you've already fetched all competitions but want a subset
 */
export function filterCompetitionsByType(
    competitions: HubCompetition[],
    type: HubCompetitionType,
): HubCompetition[] {
    return competitions.filter(c => c.type === type);
}

/**
 * Helper to check if a competition is "active" based on status
 */
export function isCompetitionActive(competition: HubCompetition): boolean {
    const status = competition.status?.toUpperCase() as typeof ACTIVE_COMPETITION_STATUSES[number];
    return ACTIVE_COMPETITION_STATUSES.includes(status);
}
